import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Container, Group, Illustration, Text } from '@melio/penny';

import { useAnalytics, type Cta } from '../../analytics';
import { getEntryPoint, getCallingFlow } from '../../session';
import { completeAuthRedirect } from '../../hooks/useRedirectParam';
import { FACTOR_COPY, type AvailableFactor } from '../MfaEnrollOfferPage/factorCopy';

export const MfaEnrollConfirmPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { track } = useAnalytics();
  const entryPoint = getEntryPoint();
  // Which flow actually produced this enrollment, set by LoginPage/SignUpPage/MFAChallengePage
  // at the moment the action started - not the visit's first-touch entry point, which can
  // genuinely differ (e.g. a login reached via the sign-up page).
  const flow = (getCallingFlow() ?? entryPoint) === 'sign-up' ? 'sign-up' : 'sign-in';

  // Only reachable via the navigate() call MFAChallengePage makes on a verified enrollment
  // success - router state doesn't survive a direct/bookmarked/replayed URL load, so this
  // redirects away rather than showing false success and consuming the stored auth redirect.
  const state = location.state as { verified?: boolean; factor?: string; hideSkip?: boolean } | null;
  const verified = Boolean(state?.verified);
  // `factor` crossed a router-state boundary as a plain string, so it isn't compiler-checked
  // against AvailableFactor the way the offer screen's own selection is - fall back to 'phone'
  // (always a valid key) rather than trusting an arbitrary string to index FACTOR_COPY.
  const factor: AvailableFactor = state?.factor && state.factor in FACTOR_COPY ? (state.factor as AvailableFactor) : 'phone';
  const copy = FACTOR_COPY[factor];
  // The skip-experiment bucket, carried over from the offer screen via router state (the
  // enrollment offer itself is already cleared by the time this screen renders) so this
  // "completion" event can still be split by arm, same as the offer screen's own events.
  const bucketProperties = { Variant: state?.hideSkip ? 'skip-hidden' : 'control' };

  document.title = copy.confirmTitle;

  useEffect(() => {
    if (!verified) {
      navigate({ pathname: '/sign-in', search: location.search }, { replace: true });
    }
  }, [verified, navigate]);

  useEffect(() => {
    if (!verified) return;
    track({
      eventName: 'Connection-View',
      properties: {
        PageName: 'phone-number-verified',
        Flow: flow,
        Intent: flow,
        EntryPoint: entryPoint,
        ...bucketProperties,
      },
    });
  }, []);

  const trackClick = (cta: Cta) => {
    track({
      eventName: 'Connection-Click',
      properties: {
        PageName: 'phone-number-verified',
        Flow: flow,
        Intent: flow,
        EntryPoint: entryPoint,
        Cta: cta,
        ...bucketProperties,
      },
    });
  };

  const handleGotIt = () => {
    trackClick('got-it');
    completeAuthRedirect();
  };

  if (!verified) {
    return null;
  }

  return (
    <Container overflow="visible" paddingY="xxl" style={{ width: '100%', maxWidth: '480px' }} data-testid="mfa-enroll-confirm-page">
      <Group variant="vertical" spacing="l" alignItems="center">
        <Illustration size="large" type="success" />

        <Group variant="vertical" spacing="xs" alignItems="center">
          <Text textStyle="heading1Semi" color="semantic.text.primary" data-testid="mfa-enroll-confirm-page-title">
            {copy.confirmTitle}
          </Text>
          <Text
            textStyle="body2"
            color="semantic.text.secondary"
            style={{ textAlign: 'center' }}
            data-testid="mfa-enroll-confirm-page-body"
          >
            {copy.confirmBody}
          </Text>
        </Group>

        <Button label="Got it" variant="primary" size="large" onClick={handleGotIt} data-testid="got-it-button" />
      </Group>
    </Container>
  );
};
